'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  CheckCircle2,
  Download,
  ExternalLink,
  FileArchive,
  FilePlus2,
  Layers3,
  LockKeyhole,
  MonitorSmartphone,
  PackageCheck,
  Palette,
  Pencil,
  Plus,
  ShieldCheck,
  Sparkles,
  Store,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadStudioProjectZip, importStudioProjectFiles, importStudioProjectZip } from '@/core/studio/export';
import {
  STUDIO_TEMPLATES,
  createStudioProject,
  studioDocumentsFor,
  type StudioTemplate,
  readStudioProjects,
  upsertStudioProject,
  hasUnexportedChanges,
  markStudioProjectExported,
  writeStudioProjects,
  type StudioProject,
} from '@/core/studio/project';
import { StudioBackupReminder } from '@/core/studio/StudioBackupReminder';

const workflow = [
  ['01', 'Utwórz projekt', 'Nadaj motywowi nazwę, opisz jego przeznaczenie i ustaw wspólną paletę marki.'],
  ['02', 'Zaprojektuj sklep', 'Edytuj nagłówek, stopkę i kluczowe widoki w tym samym builderze co Storefront.'],
  ['03', 'Pobierz pełny ZIP', 'Zapisz kompletną paczkę, przekaż ją klientowi albo otwórz ponownie w Studio.'],
];

export function StudioDashboard() {
  const [projects, setProjects] = useState<StudioProject[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string>('');
  const [newProjectName, setNewProjectName] = useState('Mój motyw');
  const [newProjectTemplate, setNewProjectTemplate] = useState<StudioTemplate>('shop');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let stored = readStudioProjects();
    if (stored.length === 0) {
      const starter = createStudioProject('Mój pierwszy motyw');
      upsertStudioProject(starter);
      stored = [starter];
    }
    setProjects(stored);
    setActiveProjectId(stored[0].id);
  }, []);

  const activeProject = useMemo(
    () => projects.find((project) => project.id === activeProjectId) || projects[0],
    [activeProjectId, projects],
  );

  const refresh = (selectedId?: string) => {
    const stored = readStudioProjects();
    setProjects(stored);
    if (selectedId) setActiveProjectId(selectedId);
  };

  const createProject = () => {
    const project = createStudioProject(newProjectName, newProjectTemplate);
    upsertStudioProject(project);
    refresh(project.id);
    setNewProjectName('Mój motyw');
    toast.success('Utworzono nowy projekt motywu.');
  };

  const updateProject = (patch: Partial<StudioProject>) => {
    if (!activeProject) return;
    let documents = activeProject.documents;
    if (patch.palette) {
      documents = Object.fromEntries(Object.entries(activeProject.documents).map(([id, page]) => [
        id,
        {
          ...page,
          content: Array.isArray(page.content)
            ? page.content.map((block: any) => block?.type === 'globals'
              ? { ...block, settings: { ...(block.settings || {}), colors: [...patch.palette!] } }
              : block)
            : page.content,
        },
      ]));
    }
    const next = { ...activeProject, ...patch, documents, updatedAt: new Date().toISOString() };
    upsertStudioProject(next);
    refresh(next.id);
  };

  const exportProject = async () => {
    if (!activeProject) return;
    setExporting(true);
    try {
      await downloadStudioProjectZip(activeProject);
      markStudioProjectExported(activeProject.id);
      refresh(activeProject.id);
      toast.success('Gotowa paczka ZIP została pobrana.');
    } catch (error) {
      console.error(error);
      toast.error('Nie udało się przygotować paczki ZIP.');
    } finally {
      setExporting(false);
    }
  };

  const importProject = async (file: File | undefined) => {
    if (!file) return;
    try {
      const project = await importStudioProjectZip(file);
      upsertStudioProject(project);
      refresh(project.id);
      toast.success('Paczka motywu została otwarta jako nowy projekt.');
    } catch (error: any) {
      toast.error(error?.message || 'Nie udało się otworzyć paczki motywu.');
    }
  };

  const importLooseProject = async (files: File[]) => {
    if (files.length === 0) return;
    try {
      const { project, imported, skipped } = await importStudioProjectFiles(files);
      upsertStudioProject(project);
      refresh(project.id);
      toast.success(`Otworzono stary eksport: ${imported} plików połączono w jeden projekt.`);
      if (skipped.length > 0) toast.warning(`Pominięto nierozpoznane pliki: ${skipped.join(', ')}`);
    } catch (error: any) {
      toast.error(error?.message || 'Nie udało się otworzyć plików JSON motywu.');
    }
  };

  const deleteProject = () => {
    if (!activeProject || projects.length <= 1) return;
    if (!window.confirm(`Usunąć lokalny projekt „${activeProject.name}”?`)) return;
    const next = projects.filter((project) => project.id !== activeProject.id);
    writeStudioProjects(next);
    setProjects(next);
    setActiveProjectId(next[0]?.id || '');
  };

  return (
    <main className="min-h-screen bg-[#0b1517] font-sans text-[#fffdee]">
      <StudioBackupReminder />
      <header className="sticky top-0 z-50 border-b border-[#269A96]/15 bg-[#0b1517]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-[76px] max-w-[1200px] items-center justify-between gap-6 px-5 md:px-8">
          <a href="https://shopstag.pl" className="group flex items-center gap-3" aria-label="ShopStag.pl">
            <Image src="/branding/logo.png" alt="ShopStag" width={52} height={52} priority className="h-12 w-12 object-contain drop-shadow-[0_0_12px_rgba(38,154,150,0.32)] transition group-hover:scale-105" />
            <div>
              <p className="text-lg font-black leading-none tracking-[-0.04em] text-white">Shop<span className="text-[#3EB0AB]">Stag</span></p>
              <p className="mt-1 text-[10px] font-extrabold uppercase tracking-[0.19em] text-white/45">Theme Studio</p>
            </div>
          </a>

          <nav className="hidden items-center gap-6 text-xs font-bold text-white/60 md:flex" aria-label="Nawigacja Studio">
            <a href="#jak-to-dziala" className="transition hover:text-[#3EB0AB]">Jak to działa</a>
            <a href="#kreator" className="transition hover:text-[#3EB0AB]">Kreator</a>
            <a href="#paczka" className="transition hover:text-[#3EB0AB]">Paczka motywu</a>
          </nav>

          <a href="https://shopstag.pl" className="inline-flex h-10 items-center gap-2 rounded-xl border border-white/15 px-3 text-xs font-extrabold text-white transition hover:border-[#3EB0AB]/60 hover:bg-[#269A96]/10 md:px-4">
            <span className="hidden sm:inline">ShopStag.pl</span><ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </header>

      <section className="relative overflow-hidden border-b border-white/5">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_78%_20%,rgba(38,154,150,0.24),transparent_34%),radial-gradient(circle_at_12%_80%,rgba(62,176,171,0.10),transparent_30%)]" />
        <div className="relative mx-auto grid max-w-[1200px] gap-12 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
          <div>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#3EB0AB]/35 bg-[#269A96]/10 px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-[#63c7c2]">
              <Sparkles className="h-4 w-4" /> Publiczna wersja beta
            </div>
            <h1 className="max-w-3xl text-4xl font-black leading-[1.04] tracking-[-0.055em] text-white md:text-6xl">
              Zaprojektuj motyw ShopStag. <span className="text-[#3EB0AB]">Bez zakładania sklepu.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-8 text-white/62 md:text-lg">
              Twórz kompletne motywy dla własnej marki albo klientów. Studio korzysta z tych samych bloków, ustawień i renderera co sklepy działające na platformie ShopStag.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#kreator" className="inline-flex h-12 items-center gap-2 rounded-xl bg-[#269A96] px-6 text-sm font-black text-white shadow-[0_10px_35px_rgba(38,154,150,0.26)] transition hover:-translate-y-0.5 hover:bg-[#3EB0AB]">
                Zacznij projektować <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#jak-to-dziala" className="inline-flex h-12 items-center gap-2 rounded-xl border border-white/15 px-6 text-sm font-bold text-white transition hover:border-white/30 hover:bg-white/5">Zobacz instrukcję</a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-xs font-semibold text-white/50">
              {['Bez konta', 'Bez sklepu', 'Pełny eksport ZIP'].map((label) => (
                <span key={label} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#3EB0AB]" /> {label}</span>
              ))}
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-md">
            <div className="absolute -inset-8 rounded-full bg-[#269A96]/15 blur-3xl" />
            <div className="relative rounded-[32px] border border-[#3EB0AB]/25 bg-[#112326]/90 p-7 shadow-2xl shadow-black/30">
              <div className="flex items-center justify-between border-b border-white/10 pb-5">
                <div className="flex items-center gap-3">
                  <Image src="/branding/logo.png" alt="" width={68} height={68} className="h-16 w-16 object-contain" />
                  <div><p className="text-xs font-black uppercase tracking-[0.16em] text-[#3EB0AB]">Motyw kompletny</p><p className="mt-1 text-xl font-black text-white">{studioDocumentsFor(activeProject?.template).length} dokumentów</p></div>
                </div>
                <FileArchive className="h-8 w-8 text-white/25" />
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3 text-xs font-bold text-white/65">
                {['Nagłówek i stopka', 'Strona główna', 'Produkt i kategoria', 'Koszyk i checkout', 'Konto i logowanie', 'Blog i kontakt'].map((label) => (
                  <div key={label} className="flex items-center gap-2 rounded-xl border border-white/8 bg-black/15 p-3"><span className="h-1.5 w-1.5 rounded-full bg-[#3EB0AB]" /> {label}</div>
                ))}
              </div>
              <div className="mt-5 flex items-start gap-3 rounded-2xl bg-[#269A96]/10 p-4 text-xs leading-5 text-white/60">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#3EB0AB]" /> Motyw zachowuje strukturę wymaganą przez Storefront, więc nie projektujesz „w ciemno”.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="jak-to-dziala" className="scroll-mt-24 border-b border-white/5 bg-[#112326]">
        <div className="mx-auto max-w-[1200px] px-5 py-14 md:px-8">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#3EB0AB]">Jak zacząć</p>
          <h2 className="mt-3 text-3xl font-black tracking-[-0.04em] text-white">Od pomysłu do paczki w trzech krokach</h2>
          <p className="mt-3 max-w-2xl leading-7 text-white/55">Nie potrzebujesz hostingu, domeny ani konta sklepu. Wszystko robisz bezpośrednio w przeglądarce.</p>
          <div className="mt-9 grid gap-4 md:grid-cols-3">
            {workflow.map(([number, title, description]) => (
              <article key={number} className="rounded-3xl border border-white/10 bg-[#0b1517]/55 p-6">
                <span className="font-mono text-xs font-black text-[#3EB0AB]">{number}</span>
                <h3 className="mt-5 text-lg font-black text-white">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-white/52">{description}</p>
              </article>
            ))}
          </div>
          <div className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-300/15 bg-amber-300/[0.06] px-5 py-4 text-sm leading-6 text-amber-50/65">
            <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-amber-200/80" />
            <p><strong className="text-amber-50">Ważne:</strong> projekty są obecnie zapisywane tylko w tej przeglądarce. Regularnie pobieraj ZIP jako kopię zapasową, szczególnie przed czyszczeniem danych przeglądarki.</p>
          </div>
        </div>
      </section>

      <section id="kreator" className="scroll-mt-24 bg-[radial-gradient(circle_at_top_left,rgba(38,154,150,0.12),transparent_30%)]">
        <div className="mx-auto max-w-[1500px] px-5 py-14 md:px-8">
          <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div><p className="text-xs font-black uppercase tracking-[0.2em] text-[#3EB0AB]">Obszar roboczy</p><h2 className="mt-3 text-3xl font-black tracking-[-0.04em] text-white">Twoje projekty motywów</h2></div>
            <div className="flex items-center gap-2 text-xs font-semibold text-white/45"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Automatyczny zapis lokalny</div>
          </div>

          <div className="grid gap-6 xl:grid-cols-[310px_minmax(0,1fr)]">
            <aside className="space-y-5">
              <section className="rounded-3xl border border-white/10 bg-[#112326]/80 p-5 shadow-2xl shadow-black/10">
                <div className="mb-4 flex items-center gap-2"><Layers3 className="h-4 w-4 text-[#3EB0AB]" /><h3 className="text-sm font-black uppercase tracking-wider">Projekty</h3></div>
                <div className="space-y-2">
                  {projects.map((project) => (
                    <button key={project.id} onClick={() => setActiveProjectId(project.id)} className={`w-full rounded-xl border px-3 py-3 text-left transition ${project.id === activeProject?.id ? 'border-[#3EB0AB]/60 bg-[#269A96]/15 text-white' : 'border-white/5 bg-black/10 text-white/50 hover:border-white/15 hover:text-white'}`}>
                      <span className="block truncate text-sm font-bold">{project.name}</span><span className="mt-1 block text-[10px] uppercase tracking-wider text-white/30">{project.slug}</span>
                    </button>
                  ))}
                </div>
                <div className="mt-4 space-y-2 border-t border-white/10 pt-4">
                  <label htmlFor="studio-project-name" className="sr-only">Nazwa nowego motywu</label>
                  <input id="studio-project-name" value={newProjectName} onChange={(event) => setNewProjectName(event.target.value)} className="h-10 w-full rounded-xl border border-white/10 bg-[#0b1517]/70 px-3 text-sm text-white outline-none focus:border-[#3EB0AB]" placeholder="Nazwa nowego motywu" />
                  <div role="group" aria-label="Szablon startowy" className="grid gap-1.5">
                    {STUDIO_TEMPLATES.map((template) => {
                      const active = newProjectTemplate === template.id;
                      return (
                        <button key={template.id} type="button" aria-pressed={active} onClick={() => setNewProjectTemplate(template.id)} className={`rounded-xl border px-3 py-2 text-left transition ${active ? 'border-[#3EB0AB]/60 bg-[#269A96]/15 text-white' : 'border-white/5 bg-black/10 text-white/50 hover:border-white/15 hover:text-white'}`}>
                          <span className="block text-xs font-bold">{template.title}</span>
                          <span className="mt-0.5 block text-[10px] leading-4 text-white/40">{template.description}</span>
                        </button>
                      );
                    })}
                  </div>
                  <button onClick={createProject} className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-white/10 text-xs font-black uppercase tracking-wider text-white transition hover:bg-white/15"><Plus className="h-4 w-4" /> Nowy motyw</button>
                </div>
              </section>
              <section className="rounded-3xl border border-[#3EB0AB]/20 bg-[#269A96]/10 p-5">
                <div className="flex items-start gap-3"><PackageCheck className="mt-0.5 h-5 w-5 text-[#3EB0AB]" /><div><p className="text-sm font-bold text-white">Zgodność ze Storefrontem</p><p className="mt-1 text-xs leading-5 text-white/50">Ten sam renderer i te same edytory bloków co w sklepach ShopStag.</p></div></div>
              </section>
            </aside>

            {activeProject && (
              <div className="space-y-6">
                {hasUnexportedChanges(activeProject) && (
                  <div role="status" className="flex flex-col gap-3 rounded-2xl border border-amber-300/30 bg-amber-300/10 px-5 py-4 text-sm text-amber-50 sm:flex-row sm:items-center">
                    <LockKeyhole className="h-5 w-5 shrink-0 text-amber-200" aria-hidden />
                    <p className="flex-1">
                      <strong>Masz zmiany niepobrane do ZIP.</strong>{' '}
                      {activeProject.exportedAt
                        ? `Ostatnia kopia: ${new Date(activeProject.exportedAt).toLocaleString('pl-PL')}.`
                        : 'Ten projekt nie ma jeszcze kopii.'}{' '}
                      Projekt istnieje tylko w tej przeglądarce.
                    </p>
                    <button onClick={exportProject} disabled={exporting} className="h-9 shrink-0 rounded-xl bg-amber-300 px-4 text-xs font-black uppercase tracking-wider text-[#0b1517] transition hover:bg-amber-200 disabled:opacity-60">
                      {exporting ? 'Pakowanie…' : 'Pobierz ZIP teraz'}
                    </button>
                  </div>
                )}
                <section className="rounded-[32px] border border-white/10 bg-[#112326]/75 p-6 shadow-2xl shadow-black/10 md:p-8">
                  <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-start">
                    <div className="max-w-2xl">
                      <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-[#3EB0AB]"><Palette className="h-4 w-4" /> Projekt motywu</div>
                      <label htmlFor="studio-theme-name" className="sr-only">Nazwa projektu motywu</label>
                      <input id="studio-theme-name" value={activeProject.name} onChange={(event) => updateProject({ name: event.target.value })} className="w-full border-0 bg-transparent p-0 text-3xl font-black tracking-tight text-white outline-none md:text-4xl" />
                      <label htmlFor="studio-theme-description" className="sr-only">Opis projektu motywu</label>
                      <textarea id="studio-theme-description" value={activeProject.description} onChange={(event) => updateProject({ description: event.target.value })} className="mt-3 min-h-16 w-full resize-none rounded-xl border border-transparent bg-transparent p-0 text-sm leading-6 text-white/50 outline-none focus:border-white/10 focus:bg-black/10 focus:p-3" placeholder="Opisz styl, branżę i odbiorców motywu…" />
                      {/* Same switch as a tenant's plan in Backoffice: a website gets no shop blocks. */}
                      <div role="group" aria-label="Rodzaj projektu" className="mt-4 inline-flex rounded-xl border border-white/10 bg-black/15 p-1">
                        {([['site', 'Strona'], ['shop', 'Sklep']] as const).map(([mode, label]) => {
                          const active = (activeProject.siteMode || 'shop') === mode;
                          return (
                            <button key={mode} type="button" aria-pressed={active} onClick={() => updateProject({ siteMode: mode })} className={`h-8 rounded-lg px-4 text-xs font-bold transition ${active ? 'bg-[#269A96] text-white' : 'text-white/50 hover:text-white'}`}>
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {projects.length > 1 && <button onClick={deleteProject} className="h-11 rounded-xl border border-rose-400/20 px-4 text-xs font-bold text-rose-300 hover:bg-rose-400/10">Usuń projekt</button>}
                      <label className="flex h-11 cursor-pointer items-center gap-2 rounded-xl border border-white/15 px-4 text-xs font-bold text-white transition hover:bg-white/10"><Upload className="h-4 w-4" /> Otwórz stare JSON-y<input type="file" accept=".json,application/json" multiple className="hidden" onChange={(event) => { void importLooseProject(Array.from(event.target.files || [])); event.currentTarget.value = ''; }} /></label>
                      <label className="flex h-11 cursor-pointer items-center gap-2 rounded-xl border border-white/15 px-4 text-xs font-bold text-white transition hover:bg-white/10"><Upload className="h-4 w-4" /> Otwórz ZIP<input type="file" accept=".zip,application/zip" className="hidden" onChange={(event) => { void importProject(event.target.files?.[0]); event.currentTarget.value = ''; }} /></label>
                      <button onClick={exportProject} disabled={exporting} className="flex h-11 items-center gap-2 rounded-xl bg-[#269A96] px-5 text-xs font-black uppercase tracking-wider text-white shadow-lg shadow-black/20 transition hover:bg-[#3EB0AB] disabled:opacity-60"><Download className="h-4 w-4" /> {exporting ? 'Pakowanie…' : 'Pobierz pełny ZIP'}</button>
                    </div>
                  </div>
                  <div className="mt-6 grid gap-3 border-t border-white/10 pt-6 sm:grid-cols-2 xl:grid-cols-5">
                    {activeProject.palette.map((color, index) => (
                      <label key={index} className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/15 p-2"><input type="color" value={color} onChange={(event) => { const next = [...activeProject.palette]; next[index] = event.target.value; updateProject({ palette: next }); }} className="h-9 w-9 cursor-pointer rounded-lg border-0 bg-transparent" /><span className="font-mono text-[11px] uppercase text-white/40">C{index + 1} {color}</span></label>
                    ))}
                  </div>
                </section>

                <section>
                  <div className="mb-4 flex items-end justify-between gap-4"><div><h3 className="text-xl font-black text-white">Elementy kompletnego motywu</h3><p className="mt-1 text-sm text-white/45">Każdy dokument otwiera pełny Page Builder ShopStag.</p></div><div className="hidden items-center gap-2 text-xs text-white/35 md:flex"><MonitorSmartphone className="h-4 w-4" /> Desktop · tablet · mobile</div></div>
                  <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
                    {studioDocumentsFor(activeProject.template).map((document) => {
                      const editedAt = activeProject.documents[document.id]?.updated_at;
                      return (
                        <Link key={document.id} href={`/studio/editor?project=${encodeURIComponent(activeProject.id)}&document=${encodeURIComponent(document.id)}`} className="group rounded-2xl border border-white/10 bg-[#112326]/65 p-5 transition hover:-translate-y-0.5 hover:border-[#3EB0AB]/45 hover:bg-[#269A96]/10">
                          <div className="flex items-start justify-between gap-4"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-[#3EB0AB] transition group-hover:bg-[#269A96] group-hover:text-white"><FilePlus2 className="h-5 w-5" /></div><Pencil className="h-4 w-4 text-white/25 transition group-hover:text-[#3EB0AB]" /></div>
                          <h4 className="mt-5 text-base font-black text-white">{document.title}</h4><p className="mt-2 min-h-10 text-xs leading-5 text-white/48">{document.description}</p><p className="mt-4 truncate font-mono text-[10px] text-white/25">{document.exportPath}</p>{editedAt && <p className="mt-2 text-[10px] text-white/25">Zapis: {new Date(editedAt).toLocaleString('pl-PL')}</p>}
                        </Link>
                      );
                    })}
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </section>

      <section id="paczka" className="scroll-mt-24 border-t border-white/5 bg-[#112326]">
        <div className="mx-auto grid max-w-[1200px] gap-8 px-5 py-14 md:px-8 lg:grid-cols-2 lg:items-center">
          <div><p className="text-xs font-black uppercase tracking-[0.2em] text-[#3EB0AB]">Format wymiany</p><h2 className="mt-3 text-3xl font-black tracking-[-0.04em] text-white">Jedna paczka, cały projekt</h2><p className="mt-4 max-w-xl text-sm leading-7 text-white/55">ZIP zawiera manifest motywu, konfigurację marki, nagłówek, stopkę oraz wszystkie szablony stron. Możesz przechowywać go jako wersję projektu lub przekazać dalej.</p></div>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              [Store, 'Pełny Storefront', 'Widoki sprzedażowe i informacyjne'],
              [Palette, 'Wspólna marka', 'Paleta zsynchronizowana w dokumentach'],
              [FileArchive, 'Przenośny ZIP', 'Eksport i ponowne otwarcie w Studio'],
              [MonitorSmartphone, 'Responsywność', 'Podgląd desktop, tablet i mobile'],
            ].map(([Icon, title, description]) => {
              const ItemIcon = Icon as typeof Store;
              return <div key={String(title)} className="rounded-2xl border border-white/10 bg-[#0b1517]/45 p-5"><ItemIcon className="h-5 w-5 text-[#3EB0AB]" /><p className="mt-4 text-sm font-black text-white">{String(title)}</p><p className="mt-1 text-xs leading-5 text-white/42">{String(description)}</p></div>;
            })}
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-[#081012]">
        <div className="mx-auto flex max-w-[1200px] flex-col justify-between gap-5 px-5 py-8 text-xs text-white/40 md:flex-row md:items-center md:px-8">
          <div className="flex items-center gap-3"><Image src="/branding/logo.png" alt="ShopStag" width={40} height={40} className="h-9 w-9 object-contain" /><span>ShopStag Theme Studio · narzędzie platformy ShopStag</span></div>
          <div className="flex flex-wrap gap-5 font-bold"><a href="https://shopstag.pl" className="text-white/55 transition hover:text-[#3EB0AB]">ShopStag.pl</a><a href="https://shopstag.pl/kontakt" className="text-white/55 transition hover:text-[#3EB0AB]">Kontakt</a></div>
        </div>
      </footer>
    </main>
  );
}
